import React, { useContext, useState, useEffect, useRef, use } from 'react';
import styled from 'styled-components';

import CheckboxSettingsField from './CheckboxSettingsField';
import InputSettingsField from './InputSettingsField';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import MetadataSettings from './MetadataSettings';
import ButtonGroupSettings from './ButtonGroupSettings';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import Button from '../../misc/Button';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import RequiredLogicSettings from './RequiredLogicSettings';
import SettingsTabs from './SettingsTabs';
import TooltipSettings from './TooltipSettings';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import TitleSettings from './TitleSettings';
import DateFormatSettings from './DateFormatSettings';
import DecoratorSettings from './DecoratorSettings';
import { StyledFieldWrapper, StyledButtonsWrapper, DividerLine, StyledAddonAfter, StyledAccordionFieldWrapper } from './SettingsCommonElements';

import FieldTypes from '../../../constants/FieldTypes';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import { isSettingHidden, isStyleSettingHidden, getMetadataOptions } from '../../../lib/settingsHelper';
import { validateReadableId } from '@builttocreate/engine-utils';
import { validateDateTimeFormat } from '../../../lib/dateTimeHelper';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.dangerColor};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 3px;
`;

/**
 * These field settings should be used for the following fields:
 * - text
 * - textarea
 * - rich text field
 * - date
 * - number
 * - uniqueId
 */
const FieldSettingsTextField = ({

  //Logic properties
  fileId,
  sortedPages,
  fields,
  logic,
  requiredLogic,

  view,
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  fieldSection,

  title,
  type,
  format,
  x,
  y,
  width,
  height,
  value,
  metadata,
  config,
  hidden,
  hiddenViews,
  disabled,
  formula,

  //API Developers
  identifier,
  required,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  //Field style
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,

  /**
   * Auto-shrink settings. This component takes its values by destructuring them out of its
   * props, so a name that is not listed here is simply not a variable inside the function:
   * the checkbox would always render unticked and the number inputs always empty, whatever
   * the document actually says.
   */
  autoShrink,
  maxLines,
  maxLength,

  /**
   * Auto-height settings. Same reason as the block above: a name that is not destructured here
   * is simply not a variable inside this function, so the checkbox would always render unticked
   * whatever the document says.
   *
   * isFillablePDFForm is supplied by File.js from the section that owns the active field, and
   * decides whether ticking the box also records the ruled line pitch to return to.
   */
  autoHeight,
  baseLineHeight,
  isFillablePDFForm,

  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  margin,
  padding,
  lineHeight,
  zIndex,

  //Action handlers
  onChange,
  onDelete,
  onDuplicate,

  tipVisible,
  tipTitle,
  tipDescription,
  useReadableIds,
  decorators,
  onDecoratorCreate,
  onDecoratorChange,
  onDecoratorDelete,

}) => {


  const theme = useContext(ThemeContext);
  const [readableIdError, setReadableIdError] = useState('');
  const [formatError, setFormatError] = useState('');

  const prevProps = useRef();

  useEffect(() => {

    if (prevProps.current && prevProps.current !== fieldId && readableIdError) {
      setReadableIdError('');
    }

    prevProps.current = fieldId;

  }, [fieldId, setReadableIdError]);


  const isStylesHidden = isSettingHidden(config, 'styles');
  const isTitleHidden = isSettingHidden(config, 'title');
  const isValueHidden = isSettingHidden(config, 'value');
  const isFormatHidden = isSettingHidden(config, 'format');
  const isDisabledHidden = isSettingHidden(config, 'disabled');
  const isRequiredHidden = isSettingHidden(config, 'required');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isLogicHidden = isSettingHidden(config, 'logic');
  const isVisibilityHidden = isSettingHidden(config, 'hiddenViews');
  const isTooltipHidden = isSettingHidden(config, 'tooltip');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isDecoratorsHidden = isSettingHidden(config, 'decorators');
  const isIdHidden = isSettingHidden(config, '_id');

  //Title Styles
  const isTitleFontSizeHidden = isStyleSettingHidden(config, 'titleFontSize');
  const isTitleFontColorHidden = isStyleSettingHidden(config, 'titleFontColor');
  const isTitleTextAlignHidden = isStyleSettingHidden(config, 'titleTextAlign');
  const isTitleFontStyleHidden = isStyleSettingHidden(config, 'titleFontStyle');
  const isTitleFontWeightHidden = isStyleSettingHidden(config, 'titleFontWeight');
  const isTitleTextTransformHidden = isStyleSettingHidden(config, 'titleTextTransform');
  const isTitleTextDecorationHidden = isStyleSettingHidden(config, 'titleTextDecoration');

  //Text Styles
  const isLineHeightHidden = isStyleSettingHidden(config, 'lineHeight');
  const isFontSizeHidden = isStyleSettingHidden(config, 'fontSize');
  const isFontColorHidden = type === FieldTypes.rte || isStyleSettingHidden(config, 'fontColor');
  const isTextAlignHidden = isStyleSettingHidden(config, 'textAlign');
  const isFontStyleHidden = isStyleSettingHidden(config, 'fontStyle');
  const isFontWeightHidden = isStyleSettingHidden(config, 'fontWeight');
  const isTextTransformHidden = isStyleSettingHidden(config, 'textTransform');
  const isTextDecorationHidden = isStyleSettingHidden(config, 'textDecoration');
  const isTextOverflowHidden = isStyleSettingHidden(config, 'textOverflow');

  /**
   * Lets an integrator hide the auto-shrink controls the same way as any other style control,
   * and hides them outright for the five field types this panel serves that the feature does
   * not apply to.
   */
  const isAutoShrinkHidden = isStyleSettingHidden(config, 'autoShrink')
    || (type !== FieldTypes.text && type !== FieldTypes.textarea);

  //Same treatment for auto-height, which applies to exactly the same two field types
  const isAutoHeightHidden = isStyleSettingHidden(config, 'autoHeight')
    || (type !== FieldTypes.text && type !== FieldTypes.textarea);

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(config, 'backgroundColor');
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');
  const isZIndexHidden = isStyleSettingHidden(config, 'zIndex');
  const isPositionHidden = isStyleSettingHidden(config, 'position');

  const isHeaderOrFooter = fieldSection === 'header' || fieldSection === 'footer';

  const metadataOptions = getMetadataOptions(config);

  /**
   * What the two number inputs show. A saved value is legitimately undefined when it is
   * coming from the file wide default, and an empty number input reads as "no limit" when the
   * field is in fact clamping at 2 lines and 100 characters. Display only: nothing is written
   * back until the checkbox is ticked or an input is edited.
   */
  const displayedMaxLines = typeof maxLines === 'number' ? maxLines : 2;
  const displayedMaxLength = typeof maxLength === 'number' ? maxLength : 100;

  const handleAutoShrinkToggle = (selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, {
    autoShrink: selected,

    /**
     * Write the defaults on first tick, so the saved document states plainly what it is doing
     * rather than relying on an invisible fallback. Existing values are left alone.
     */
    maxLines: selected && typeof maxLines !== 'number' ? 2 : maxLines,
    maxLength: selected && typeof maxLength !== 'number' ? 100 : maxLength,
  });

  const handleMaxLinesChange = (id, value) => {
    //Whole numbers only, never below 1: zero visible lines would hide the text completely
    const next = Math.max(1, parseInt(value, 10) || 1);
    onChange(fieldId, fieldIdentifier, fieldPositionId, { maxLines: next });
  };

  const handleMaxLengthChange = (id, value) => {
    //This is the point at which shrinking stops. It never limits how much can be typed.
    const next = Math.max(1, parseInt(value, 10) || 1);
    onChange(fieldId, fieldIdentifier, fieldPositionId, { maxLength: next });
  };

  /**
   * Only a textarea on a fillable PDF section draws ruled lines, so only there is there a pitch
   * worth recording.
   */
  const capturesLinePitch = type === FieldTypes.textarea && isFillablePDFForm === true;

  /**
   * Ticking the box records the field as it is. height and lineHeight here are the unscaled
   * fieldPosition values, which is what gets persisted.
   *
   * baseHeight is rewritten on every tick: it is what the user drew, and the web SDK only shows
   * it. baseLineHeight is different. Auto-height rescales lineHeight on every change so the
   * printed pitch stays put, and the auto-shrink line cap on a fillable textarea is frozen at
   * the base pitch (see TextareaField). After a shrink to one line the persisted lineHeight is
   * the pitch of ONE rule, and recording that as the base on a re-tick would pin the cap, and
   * the font, to one line for good. So a finite base is kept. It can only be the user's own
   * choice: the line-height input below writes the base alongside lineHeight, and that input is
   * locked while auto-height is on.
   *
   * Unticking sends the switch alone. Leaving height and the bases where they are keeps the
   * field looking exactly as it did, and nothing is ever sent as an explicit undefined,
   * which would land in the changelog as a real key.
   */
  const handleAutoHeightToggle = (selected) => onChange(fieldId, fieldIdentifier, fieldPositionId,
    selected
      ? {
        autoHeight: true,
        baseHeight: height,
        ...(capturesLinePitch ? { baseLineHeight: Number.isFinite(baseLineHeight) ? baseLineHeight : (lineHeight || 10) } : {}),
      }
      : { autoHeight: false });

  /**
   * The Text accordion's height is a fixed pixel number, not content driven, so it has to grow
   * or the new controls are clipped below the fold. Roughly 34px per control row: one for the
   * checkbox, two more once it is ticked.
   */
  const textAccordionHeight = (type === FieldTypes.textarea ? 210 : 170)
    + (isAutoShrinkHidden ? 0 : 34)
    + (autoShrink && !isAutoShrinkHidden ? 68 : 0)
    + (isAutoHeightHidden ? 0 : 34);


  let nextFieldSettingsIcon = '';
  if (type === FieldTypes.text) nextFieldSettingsIcon = ThemeIcons.fields.text;
  if (type === FieldTypes.number) nextFieldSettingsIcon = ThemeIcons.fields.number;
  if (type === FieldTypes.date) nextFieldSettingsIcon = ThemeIcons.fields.date;
  if (type === FieldTypes.textarea) nextFieldSettingsIcon = ThemeIcons.fields.textarea;

  const defaultValueNode = isValueHidden || (type !== FieldTypes.text && type !== FieldTypes.textarea) ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="Default"
        fieldId={fieldId}
        value={value}
        type={type}
        rows={3}
        onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { value })}
      />
    </StyledFieldWrapper>
  );


  const readableIdNode = isIdHidden || !useReadableIds ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="ID"
        fieldId={fieldId}
        value={fieldId}
        rows={3}
        error={readableIdError ? true : false}
        disabled={!useReadableIds}
        onBlur={(value) => {

          if (value && !validateReadableId(value, fields) && value !== fieldId) {

            setReadableIdError(`Invalid: Can't be duplicate or start with numerics, use reserved ES6 keywords, or have spaces.`);
          
          } else {

            setReadableIdError('');

            if (value && value !== fieldId) {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { _id: value });
            }

          }
        }}
      />
      {readableIdError && (
        <StyledErrorMessage theme={theme}>
          {readableIdError}
        </StyledErrorMessage>
      )}
    </StyledFieldWrapper>
  );

  const formatNode = isFormatHidden || type !== FieldTypes.date ? null : (
    <>
      <DateFormatSettings
        format={format}
        onChange={(value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { format: value })}
        customDateTimeFormatEnabled={config?.customDateTimeFormat}
      />
      <DividerLine />
    </>
  );

  const stylesNode = (
    <>
      {(isTitleFontSizeHidden
        && isTitleFontColorHidden
        && isTitleTextAlignHidden
        && isTitleFontStyleHidden
        && isTitleFontWeightHidden
        && isTitleTextTransformHidden
        && isTitleTextDecorationHidden) ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Title"
            height={170}
          >
            <FieldStyleSettings
              id={`${fieldId}-title`}
              {...{
                fontSize: titleFontSize,
                onFontSizeChange: isTitleFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontSize: value }),

                fontColor: titleFontColor,
                onFontColorChange: isTitleFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontColor: value }),

                textAlign: titleTextAlign,
                onTextAlignChange: isTitleTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextAlign: value }),

                fontStyle: titleFontStyle,
                onFontStyleChange: isTitleFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontStyle: value }),

                fontWeight: titleFontWeight,
                onFontWeightChange: isTitleFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontWeight: value }),

                textTransform: titleTextTransform,
                onTextTransformChange: isTitleTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextTransform: value }),

                textDecoration: titleTextDecoration,
                onTextDecorationChange: isTitleTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextDecoration: value }),
              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {(
        isFontSizeHidden
        && isFontColorHidden
        && isTextAlignHidden
        && isFontStyleHidden
        && isFontWeightHidden
        && isTextTransformHidden
        && isTextDecorationHidden
        && isTextOverflowHidden

        /**
         * Without this the auto-shrink controls would disappear whenever every other text
         * style is hidden, because the whole section collapses.
         */
        && isAutoShrinkHidden
        && isAutoHeightHidden
      ) ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Text"
            height={textAccordionHeight}
          >
            <FieldStyleSettings
              id={`${fieldId}-field`}
              {...{

                lineHeight: lineHeight || 10,
                onLineHeightChange: isLineHeightHidden || (type !== FieldTypes.textarea && type !== FieldTypes.rte) ? null : (value) => {

                  /**
                   * On a fillable PDF textarea a pitch typed here is the user's chosen base as
                   * well, so both are written together. This input is locked while auto-height
                   * is on, which is what keeps a rescaled pitch from ever becoming the base.
                   */
                  if (value) onChange(fieldId, fieldIdentifier, fieldPositionId, capturesLinePitch ? { lineHeight: value, baseLineHeight: value } : { lineHeight: value });

                },

                /**
                 * Auto-height owns the line height on a fillable PDF textarea: it rescales the
                 * percentage on every change so the printed pitch stays put. A value typed here
                 * would be overwritten by the next blur, so the input is shown locked rather
                 * than left to look editable.
                 */
                lineHeightLocked: autoHeight === true,

                fontSize,
                onFontSizeChange: isFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontSize: value }),

                fontColor,
                onFontColorChange: isFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontColor: value }),

                textAlign,
                onTextAlignChange: isTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textAlign: value }),

                fontStyle,
                onFontStyleChange: isFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontStyle: value }),

                fontWeight,
                onFontWeightChange: isFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontWeight: value }),

                textTransform,
                onTextTransformChange: isTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textTransform: value }),

                textDecoration,
                onTextDecorationChange: isTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textDecoration: value }),

                textOverflow,
                onTextOverflowChange: isTextOverflowHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textOverflow: value }),

              }}
            />
            {isAutoShrinkHidden ? null : (
              <>
                <StyledAccordionFieldWrapper>
                  <CheckboxSettingsField
                    icon={ThemeIcons.settings.fontSize}
                    iconColor={autoShrink ? theme.primaryColor : theme.borderColor}
                    id="autoShrink"
                    label="Auto shrink text"
                    selected={autoShrink === true}
                    onChange={handleAutoShrinkToggle}
                  />
                </StyledAccordionFieldWrapper>
                {!autoShrink ? null : (
                  <>
                    <StyledAccordionFieldWrapper>
                      <InputSettingsField
                        type="number"
                        addonAfter={<StyledAddonAfter>Max lines</StyledAddonAfter>}
                        value={displayedMaxLines}
                        onChange={handleMaxLinesChange}
                      />
                    </StyledAccordionFieldWrapper>
                    <StyledAccordionFieldWrapper>
                      <InputSettingsField
                        type="number"
                        addonAfter={<StyledAddonAfter>Max length</StyledAddonAfter>}
                        value={displayedMaxLength}
                        onChange={handleMaxLengthChange}
                      />
                    </StyledAccordionFieldWrapper>
                  </>
                )}
              </>
            )}
            {isAutoHeightHidden ? null : (
              <StyledAccordionFieldWrapper>
                <CheckboxSettingsField
                  icon={ThemeIcons.settings.fontSize}
                  iconColor={autoHeight ? theme.primaryColor : theme.borderColor}
                  id="autoHeight"
                  label="Auto height"
                  selected={autoHeight === true}
                  onChange={handleAutoHeightToggle}
                />
              </StyledAccordionFieldWrapper>
            )}
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {(
        isBorderColorHidden
        && isBorderRadiusHidden
        && isBorderWidthHidden
        && isBackgroundColorHidden
        && isMarginHidden
        && isPaddingHidden
        && isPositionHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Field"
          height={400}
        >
          <FieldStyleSettings
            id={`${fieldId}-field`}
            {...{

              borderColor,
              onBorderColorChange: isBorderColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderColor: value }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderRadius: value }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderWidth: value }),

              backgroundColor,
              onBackgroundColorChange: isBackgroundColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { backgroundColor: value }),

              padding,
              onPaddingChange: isPaddingHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { padding: value }),

              margin,
              onMarginChange: isMarginHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { margin: value }),

              zIndex,
              onZIndexChange: isZIndexHidden ? null : (value) => (value ? onChange(fieldId, fieldIdentifier, fieldPositionId, { zIndex: value }) : ''),

            }}
          />
          {isPositionHidden ? null : (
            <PositionSettings
              {...{
                view,
                x,
                y,
                width,
                height,

                //Auto-height writes the height on every blur, so it must not also be typeable
                heightLocked: autoHeight === true,
                onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { [field]: value })
              }}
            />
          )}
        </AccordionSettings>
      )}
    </>
  );

  const settingsNode = (
    <>
      {isTitleHidden ? null : (
        <>
          <SettingsLabel>Title</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <TitleSettings
            {...{
              title,
              titleDisplay
            }}
            onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
          />
        </>
      )}
      {(isValueHidden
        && isFormatHidden
        && isRequiredHidden
        && isDisabledHidden
      ) ? null : (
        <>
          <SettingsLabel>Field</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
        </>
      )}
      {defaultValueNode}
      {formatNode}
      {isDisabledHidden ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.disabled}
            iconColor={disabled ? theme.primaryColor : theme.borderColor}
            id="disabled"
            label="Read-only"
            selected={disabled}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { disabled: selected },)}
          />
        </StyledFieldWrapper>
      )}
      {isRequiredHidden ? null : (
        !isHeaderOrFooter && (
          <StyledFieldWrapper>
            <CheckboxSettingsField
              icon={ThemeIcons.settings.required}
              iconColor={required ? theme.dangerColor : theme.borderColor}
              iconSize="1x"
              id="required"
              label="Required"
              selected={required}
              onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { required: selected },)}
            />
          </StyledFieldWrapper>
        )
      )}
      {isHeaderOrFooter || fields.length < 2 ? null : (
        <>
          <SettingsLabel>Required Logic</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledFieldWrapper>
            <RequiredLogicSettings
              fileId={fileId}
              targetLabel=" field"
              targetFieldId={fieldId}
              sortedPages={sortedPages}
              fields={fields}
              logic={requiredLogic}
              onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { requiredLogic: nextLogic })}
            />
          </StyledFieldWrapper>
        </>
      )}
      {(isHiddenHidden && (isLogicHidden || fields.length < 2)) ? null : (
        !isHeaderOrFooter && (
          <>
            <SettingsLabel>Conditional Logic</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
            {isLogicHidden || fields.length < 2 ? null : (
              <StyledFieldWrapper>
                <ConditionalLogicSettings
                  fileId={fileId}
                  targetLabel=" field"
                  targetFieldId={fieldId}
                  sortedPages={sortedPages}
                  fields={fields}
                  logic={logic}
                  onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { logic: nextLogic })}
                />
              </StyledFieldWrapper>
            )}
            {isHiddenHidden ? null : (
              <StyledFieldWrapper>
                <CheckboxSettingsField
                  icon={ThemeIcons.settings.hide}
                  iconColor={hidden ? theme.warningColor : theme.borderColor}
                  id="hidden"
                  label="Hidden"
                  selected={hidden}
                  onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { hidden: selected },)}
                />
              </StyledFieldWrapper>
            )}
          </>
        )
      )}
      {isVisibilityHidden ? null : (
        !isHeaderOrFooter && (
          <>
            <SettingsLabel>Visibility</SettingsLabel>
            <DividerLine style={{ marginBottom: 14 }} />
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('desktop') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsDesktop"
                label="Hide on desktop"
                selected={hiddenViews?.includes('desktop') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'desktop']
                    : (hiddenViews || []).filter(v => v !== 'desktop');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('mobile') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsMobile"
                label="Hide on mobile"
                selected={hiddenViews?.includes('mobile') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'mobile']
                    : (hiddenViews || []).filter(v => v !== 'mobile');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hiddenViews?.includes('pdf') ? theme.warningColor : theme.borderColor}
                id="hiddenViewsPdf"
                label="Hide on pdf"
                selected={hiddenViews?.includes('pdf') || false}
                onChange={(selected) => {
                  const next = selected
                    ? [...(hiddenViews || []), 'pdf']
                    : (hiddenViews || []).filter(v => v !== 'pdf');
                  onChange(fieldId, fieldIdentifier, fieldPositionId, { hiddenViews: next });
                }}
              />
            </StyledFieldWrapper>
          </>
        )
      )}
      {isDecoratorsHidden ? null : (
        <>
          <SettingsLabel>Decorators</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <DecoratorSettings
            decorators={decorators}
            onCreate={(data) => onDecoratorCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
            onChange={(decoratorAction, data) => onDecoratorChange(fieldId, fieldIdentifier, fieldPositionId, decoratorAction, data)}
            onDelete={(decoratorAction) => onDecoratorDelete(fieldId, fieldIdentifier, fieldPositionId, decoratorAction)}
          />
        </>
      )}
      {!metadataOptions ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(fieldId, fieldIdentifier, fieldPositionId, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      {readableIdNode ? (
        <>
          <DividerLine />
          {readableIdNode}
        </>
      ) : null}
      {isTooltipHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title="Tooltip"
            height={166}
          >
            <TooltipSettings
              {...{
                tipVisible,
                tipTitle,
                tipDescription
              }}
              onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
            />
          </AccordionSettings>
        </>
      )}
      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifier'}
            height={60}
          >
            <IdentifierSelectorSettings
              fieldId={fieldId}
              fieldType={type}
              type="text"
              customValue={config?.identifier?.options}
              enableCustomOption={config?.identifier?.custom}
              value={identifier}
              onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { identifier: value })}
            />
          </AccordionSettings>
        </>
      )}
      {isDeleteHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
            />

            <Button
              icon={ThemeIcons.settings.clone}
              text="Duplicate"
              onClick={() => onDuplicate(fieldId, fieldPositionId)}
              solid
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={nextFieldSettingsIcon}
      />
      <SettingsTabs
        settings={settingsNode}
        styles={isStylesHidden ? null : stylesNode}
      />
    </StyledWrapper>
  );

};

export default FieldSettingsTextField;
